import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, MousePointerClick, Paperclip, SlidersHorizontal, AlertTriangle, Download } from 'lucide-react'
import { api, eventBadge, relTime, statusBadge } from '../lib/api'

const STATUSES = ['', 'accepted', 'queued', 'sent', 'failed', 'blocked']
function fmtBytes(n: number) {
  if (!n) return '0 B'
  const u = ['B', 'KB', 'MB', 'GB']; const i = Math.floor(Math.log(n) / Math.log(1024))
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`
}
const PAGE = 25

/**
 * The message log.
 *
 * This is the screen a customer opens when something has gone wrong, so it is built around
 * finding one message fast rather than browsing all of them: filter by status, search by
 * recipient or subject, then open the row to see the per-recipient outcome and the SMTP
 * response the receiver actually gave.
 */
export function Messages() {
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  // Mailgun-style filters.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sort, setSort] = useState<'desc' | 'asc'>('desc')
  // Source of the message: '', 'api', 'smtp', or 'dashboard'. Select 'api' to see only the
  // traffic your API key produced — the developer's own hits, isolated from dashboard/SMTP.
  const [channel, setChannel] = useState('')
  const [excludeTo, setExcludeTo] = useState('')
  // Delivery-log filter: the per-recipient outcome (delivered/bounced/opened/…), distinct from status.
  const [delivery, setDelivery] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['messages', status, query, page, from, to, dateFrom, dateTo, sort, channel, excludeTo, delivery],
    queryFn: () => api.messages.list({
      status: status || undefined,
      search: query || undefined,
      from: from.trim() || undefined,
      to: to.trim() || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      channel: channel || undefined,
      exclude_to: excludeTo.trim() || undefined,
      delivery: delivery || undefined,
      sort,
      limit: PAGE,
      offset: page * PAGE,
    }),
  })

  const activeFilters = [from, to, dateFrom, dateTo, channel, excludeTo, delivery].filter(Boolean).length + (sort === 'asc' ? 1 : 0)
  function clearFilters() { setFrom(''); setTo(''); setDateFrom(''); setDateTo(''); setChannel(''); setExcludeTo(''); setDelivery(''); setSort('desc'); setPage(0) }

  const [exporting, setExporting] = useState(false)
  // Export the WHOLE filtered result set (not just the current page) to a CSV that opens
  // directly in Excel. Honours every active filter so the report matches what's on screen.
  async function exportCsv() {
    setExporting(true)
    try {
      const all = await api.messages.list({
        status: status || undefined, search: query || undefined,
        from: from.trim() || undefined, to: to.trim() || undefined,
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
        channel: channel || undefined, exclude_to: excludeTo.trim() || undefined,
        delivery: delivery || undefined,
        sort, limit: 5000, offset: 0,
      })
      const cols = ['Sent at', 'From', 'To', 'Subject', 'Status', 'Recipients', 'Delivered', 'Opened', 'Clicked', 'Bounced', 'Reason', 'Message ID']
      const esc = (v: any) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
      const eff = (m: any) => m.opened ? 'Opened' : m.clicked ? 'Clicked' : m.bounced ? 'Bounced' : m.delivered ? 'Delivered' : m.status
      const lines = [cols.join(',')]
      for (const m of (all.data ?? [])) {
        lines.push([new Date(m.created_at).toISOString(), m.from, m.to ?? '', m.subject ?? '', eff(m),
          m.recipients, m.delivered ? 'yes' : '', m.opened ? 'yes' : '', m.clicked ? 'yes' : '',
          m.bounced ? 'yes' : '', m.reason ?? '', m.id].map(esc).join(','))
      }
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `itsbingmybusiness-messages-${new Date().toISOString().slice(0, 10)}.csv`
      a.click(); URL.revokeObjectURL(a.href)
    } finally { setExporting(false) }
  }

  const rows = data?.data ?? []
  const total = data?.total
  const hasMore = total !== undefined ? (page + 1) * PAGE < total : rows.length === PAGE

  function submitSearch(e: React.FormEvent) {
    e.preventDefault()
    setQuery(search.trim())
    setPage(0)
  }

  return (
    <div>
      <h1>Messages</h1>
      <p className="lede">
        Every message this account has submitted, with the outcome for each recipient.
        Retained for 30 days.
      </p>

      <div className="toolbar">
        <form onSubmit={submitSearch} className="row" style={{ flex: '1 1 260px' }}>
          <input
            placeholder="Search recipient or subject…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search messages"
          />
          <button type="submit" className="secondary">Search</button>
        </form>

        <div className="segmented" role="tablist" aria-label="Filter by status">
          {STATUSES.map(s => {
            const c = data?.status_counts
            const n = c ? (s === '' ? c.all : (c as any)[s]) : undefined
            return (
              <button
                key={s || 'all'}
                role="tab"
                aria-selected={status === s}
                className={status === s ? 'active' : ''}
                onClick={() => { setStatus(s); setPage(0) }}
              >
                {s === '' ? 'All' : (statusBadge(s).text)}
                {n !== undefined && <span className="tab-count">{n.toLocaleString()}</span>}
              </button>
            )
          })}
        </div>

        {/* Source (submit channel) as a visible quick-filter next to the status tabs — API vs
            SMTP vs Dashboard vs System — with live counts, so a developer can see and isolate
            exactly how much of the traffic each channel produced. */}
        <select className="src-filter" value={channel} aria-label="Filter by source"
                onChange={e => { setChannel(e.target.value); setPage(0) }}>
          {(() => {
            const c = data?.source_counts
            const all = c ? c.api + c.smtp + c.dashboard + c.system : undefined
            const lbl = (name: string, n?: number) => n === undefined ? name : `${name} (${n.toLocaleString()})`
            return (
              <>
                <option value="">{lbl('All sources', all)}</option>
                <option value="api">{lbl('API', c?.api)}</option>
                <option value="smtp">{lbl('SMTP', c?.smtp)}</option>
                <option value="dashboard">{lbl('Dashboard', c?.dashboard)}</option>
                <option value="system">{lbl('System', c?.system)}</option>
              </>
            )
          })()}
        </select>

        {/* Delivery log: the per-recipient outcome / engagement — distinct from the message
            status tabs above. Delivered, bounced, deferred, complained, suppressed, or the
            open/click engagement events. */}
        <select className="src-filter" value={delivery} aria-label="Filter by delivery log"
                onChange={e => { setDelivery(e.target.value); setPage(0) }}>
          <option value="">All delivery</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="deferred">Deferred</option>
          <option value="complained">Complained</option>
          <option value="suppressed">Suppressed</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
        </select>

        <button type="button" className={`secondary ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(v => !v)}>
          <SlidersHorizontal size={15} /> Filters{activeFilters > 0 ? ` (${activeFilters})` : ''}
        </button>

        <button type="button" className="secondary" onClick={exportCsv} disabled={exporting} title="Export the filtered log to CSV (opens in Excel)">
          <Download size={15} /> {exporting ? 'Exporting…' : 'Export'}
        </button>
      </div>

      {showFilters && (
        <div className="filter-panel">
          <div className="filter-grid">
            <label className="filter-field">
              <span>From</span>
              <input placeholder="sender@…" value={from} onChange={e => { setFrom(e.target.value); setPage(0) }} />
            </label>
            <label className="filter-field">
              <span>To (recipient)</span>
              <input placeholder="recipient@…" value={to} onChange={e => { setTo(e.target.value); setPage(0) }} />
            </label>
            <label className="filter-field">
              <span>Exclude recipient</span>
              <input placeholder="hide mail to this address" value={excludeTo} onChange={e => { setExcludeTo(e.target.value); setPage(0) }} />
            </label>
            <label className="filter-field">
              <span>From date</span>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0) }} />
            </label>
            <label className="filter-field">
              <span>To date</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0) }} />
            </label>
            <label className="filter-field">
              <span>Order</span>
              <div className="segmented">
                <button type="button" className={sort === 'desc' ? 'active' : ''} onClick={() => { setSort('desc'); setPage(0) }}>Newest first</button>
                <button type="button" className={sort === 'asc' ? 'active' : ''} onClick={() => { setSort('asc'); setPage(0) }}>Oldest first</button>
              </div>
            </label>
          </div>
          {activeFilters > 0 && (
            <button type="button" className="ghost small" onClick={clearFilters} style={{ marginTop: 10 }}>Clear filters</button>
          )}
        </div>
      )}

      {query && (
        <p className="small muted">
          Filtered by “{query}”.{' '}
          <button className="link" onClick={() => { setQuery(''); setSearch(''); setPage(0) }}>
            Clear
          </button>
        </p>
      )}

      {isLoading && <div className="skeleton" style={{ height: 260 }} />}

      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">✉</div>
          <p><strong>{query || status ? 'Nothing matches that filter' : 'No messages yet'}</strong></p>
          <p>
            {query || status
              ? 'Try clearing the filter or widening the search.'
              : 'Verify a domain, then send your first message through the API or SMTP.'}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className={`card flush ${isFetching ? 'fetching' : ''}`}>
            <table className="data">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Activity</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(m => {
                  // Furthest-along state wins: Opened → Clicked → Delivered → Bounced → raw status.
                  const eff = m.opened ? { cls: 'info', text: 'Opened' }
                    : m.clicked ? { cls: 'info', text: 'Clicked' }
                    : m.bounced ? { cls: 'failed', text: 'Bounced' }
                    : m.delivered ? { cls: 'ok', text: 'Delivered' }
                    : statusBadge(m.status)
                  return (
                    <tr key={m.id}>
                      <td>
                        <Link to={`/messages/${m.id}`} className="strong">
                          {m.subject || '(no subject)'}
                        </Link>
                        {m.channel && (
                          <span className="badge neutral" style={{ marginLeft: 8, fontSize: 10.5, textTransform: 'uppercase' }}>
                            {m.channel}
                          </span>
                        )}
                        {m.reason && (
                          <div className="small truncate" title={m.reason}
                               style={{ color: 'var(--danger)', marginTop: 2, maxWidth: 460 }}>
                            {m.reason}
                          </div>
                        )}
                      </td>
                      <td className="mono small truncate">{m.from}</td>
                      <td className="mono small truncate">
                        {m.to || '—'}
                        {m.recipients > 1 && <span className="muted"> +{m.recipients - 1}</span>}
                      </td>
                      <td><span className={`badge ${eff.cls}`}>{eff.text}</span></td>
                      <td>
                        <span className="msg-activity">
                          {m.opened && <span className="on" title="Opened"><Eye size={15} /></span>}
                          {m.clicked && <span className="on" title="Clicked"><MousePointerClick size={15} /></span>}
                          {m.has_attachments && <span title="Has attachment"><Paperclip size={15} /></span>}
                          {!m.opened && !m.clicked && !m.has_attachments && <span className="muted">—</span>}
                        </span>
                      </td>
                      <td className="small muted" title={new Date(m.created_at).toLocaleString()}>
                        {relTime(m.created_at)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button className="secondary" disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}>
              ← Newer
            </button>
            <span className="small muted">
              {total !== undefined
                ? `${page * PAGE + 1}–${Math.min((page + 1) * PAGE, total)} of ${total.toLocaleString()}`
                : `Page ${page + 1}`}
            </span>
            <button className="secondary" disabled={!hasMore} onClick={() => setPage(p => p + 1)}>
              Older →
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ==================================================================== detail

export function MessageDetail() {
  const { id = '' } = useParams()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['message', id],
    queryFn: () => api.messages.get(id),
    enabled: !!id,
  })

  const retry = useMutation({
    mutationFn: () => api.messages.retry(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['message', id] })
      await qc.invalidateQueries({ queryKey: ['messages'] })
    },
  })

  if (isLoading) return <div className="skeleton" style={{ height: 320 }} />

  if (error || !data) {
    return (
      <div>
        <p className="small"><Link to="/messages">← Messages</Link></p>
        <div className="empty">
          <div className="big">?</div>
          <p><strong>Message not found</strong></p>
          <p>It may have passed the 30-day retention window.</p>
        </div>
      </div>
    )
  }

  const recipients: any[] = data.recipients ?? []
  const events: any[] = data.events ?? []
  // Show the EFFECTIVE state as the headline badge, not the message-level status. A message row
  // stays "Sent" while its recipient progresses to Delivered → Opened; showing the raw message
  // status here made delivered mail look stuck on "Sent".
  const anyDelivered = recipients.some((r: any) => r.status === 'delivered')
  const anyBounced = recipients.some((r: any) => r.status === 'bounced')
  const s = data.opened > 0 ? { cls: 'info', text: 'Opened' }
    : data.clicked > 0 ? { cls: 'info', text: 'Clicked' }
    : anyBounced ? { cls: 'failed', text: 'Bounced' }
    : anyDelivered ? { cls: 'ok', text: 'Delivered' }
    : statusBadge(data.status)

  return (
    <div>
      <p className="small"><Link to="/messages">← Messages</Link></p>

      <div className="spread" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ marginBottom: 6 }}>{data.subject || '(no subject)'}</h1>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span className={`badge ${s.cls}`}>{s.text}</span>
            {data.opened > 0 && <span className="badge ok">Opened{data.opened > 1 ? ` ${data.opened}×` : ''}</span>}
            {data.clicked > 0 && <span className="badge ok">Clicked{data.clicked > 1 ? ` ${data.clicked}×` : ''}</span>}
            {data.has_attachments && <span className="badge neutral">Has attachment</span>}
          </div>
        </div>
        {data.status === 'failed' && (
          <button onClick={() => retry.mutate()} disabled={retry.isPending}>
            {retry.isPending ? 'Retrying…' : retry.isSuccess ? 'Re-queued ✓' : 'Retry'}
          </button>
        )}
      </div>
      {(() => {
        const bounced = recipients.filter((r: any) => r.status === 'bounced')
        const suppressed = recipients.filter((r: any) => r.suppressed_reason)
        const why = data.failure_reason
          || (bounced[0]?.response ? `${bounced[0].smtp_code ?? ''} ${bounced[0].response}`.trim() : null)
          || (suppressed[0]?.suppressed_reason ? `Recipient is on the suppression list (${suppressed[0].suppressed_reason}).` : null)
        const isFail = data.status === 'failed' || data.status === 'blocked' || bounced.length > 0 || suppressed.length > 0
        if (!isFail || !why) return null
        const title = bounced.length > 0 ? 'This message bounced'
          : data.status === 'blocked' ? 'This message was blocked'
          : suppressed.length > 0 ? 'Recipient suppressed'
          : 'This message failed'
        return (
          <div className="alert error" style={{ marginTop: 14 }}>
            <span className="ai"><AlertTriangle size={18} /></span>
            <div style={{ minWidth: 0 }}>
              <strong>{title}</strong> — the receiver’s own words:
              <div className="small mono" style={{ marginTop: 5, wordBreak: 'break-word' }}>{why}</div>
            </div>
          </div>
        )
      })()}

      {retry.isError && (
        <div className="alert error" style={{ marginTop: 12 }}>
          {(retry.error as any)?.message ?? 'Could not retry.'}
        </div>
      )}
      {retry.isSuccess && (
        <div className="alert info" style={{ marginTop: 12 }}>
          Re-queued for delivery. It also retries automatically up to 3 times on a
          transient failure.
        </div>
      )}

      <div className="card flush" style={{ marginTop: 18 }}>
        <table className="kv">
          <tbody>
            <tr><td>Message ID</td><td className="mono">{data.id}</td></tr>
            <tr><td>From</td><td className="mono">{data.from ?? data.from_address}</td></tr>
            {data.reply_to && <tr><td>Reply-To</td><td className="mono">{data.reply_to}</td></tr>}
            <tr><td>Recipients</td><td>{data.recipients_count ?? recipients.length}</td></tr>
            <tr><td>Attachments</td><td>{data.has_attachments ? 'Yes' : 'None'}</td></tr>
            <tr>
              <td>Opened</td>
              <td>{data.track_opens
                ? (data.opened > 0 ? `Yes · ${data.opened} time${data.opened > 1 ? 's' : ''}` : 'Not opened yet')
                : <span className="muted">Open tracking off — needs an HTML body</span>}</td>
            </tr>
            <tr>
              <td>Clicked</td>
              <td>{data.track_clicks
                ? (data.clicked > 0 ? `Yes · ${data.clicked} time${data.clicked > 1 ? 's' : ''}` : 'No clicks yet')
                : <span className="muted">Click tracking off</span>}</td>
            </tr>
            <tr>
              <td>Submitted</td>
              <td>{new Date(data.created_at).toLocaleString()}</td>
            </tr>
            {data.submit_channel && (
              <tr><td>Submitted via</td><td>{data.submit_channel}</td></tr>
            )}
            {data.sending_ip && (
              <tr>
                <td>Sent from IP</td>
                <td className="mono">{data.sending_ip}</td>
              </tr>
            )}
            {data.dkim_selector && (
              <tr><td>DKIM selector</td><td className="mono">{data.dkim_selector}</td></tr>
            )}
            {data.return_path && (
              <tr>
                <td>Return path</td>
                <td className="mono small">{data.return_path}</td>
              </tr>
            )}
            {data.mta_queue_id && (
              <tr>
                <td>MTA queue ID</td>
                <td className="mono">{data.mta_queue_id}</td>
              </tr>
            )}
            {data.failure_reason && (
              <tr>
                <td>Failure</td>
                <td className="crit">{data.failure_reason}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data.attachments?.length > 0 && (
        <>
          <h2>Attachments ({data.attachments.length})</h2>
          <div className="card flush">
            <table className="data">
              <thead><tr><th>File</th><th>Type</th><th className="num">Size</th><th></th></tr></thead>
              <tbody>
                {data.attachments.map((a: any) => (
                  <tr key={a.index}>
                    <td>
                      <span className="row" style={{ gap: 7, alignItems: 'center' }}>
                        <Paperclip size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <span className="strong">{a.filename || '(unnamed)'}</span>
                      </span>
                    </td>
                    <td className="small mono muted">{a.content_type || '—'}</td>
                    <td className="num">{fmtBytes(a.size_bytes)}</td>
                    <td className="right">
                      <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <a className="btn small secondary" target="_blank" rel="noopener"
                           href={`/v1/messages/${data.id}/attachments/${a.index}?inline=1`}>View</a>
                        <a className="btn small" href={`/v1/messages/${data.id}/attachments/${a.index}`}
                           download={a.filename || true}>Download</a>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(data.text_body || data.html_body) && (
        <>
          <h2>Body</h2>
          <div className="card">
            {data.html_body
              ? (
                <>
                  {/* Render the actual email as the recipient saw it. Fully sandboxed
                      (sandbox="") so the customer's HTML cannot run scripts or navigate
                      the dashboard — it is untrusted content being previewed. */}
                  <iframe
                    title="Message preview"
                    sandbox=""
                    srcDoc={data.html_body}
                    style={{ width: '100%', minHeight: 340, border: '1px solid var(--line)',
                             borderRadius: 8, background: '#fff' }}
                  />
                  <details style={{ marginTop: 12 }}>
                    <summary className="small muted" style={{ cursor: 'pointer' }}>View HTML source</summary>
                    <pre className="code" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{data.html_body}</pre>
                  </details>
                  {data.text_body && (
                    <details style={{ marginTop: 8 }}>
                      <summary className="small muted" style={{ cursor: 'pointer' }}>Plain-text version</summary>
                      <pre className="code" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{data.text_body}</pre>
                    </details>
                  )}
                </>
              )
              : (
                <pre className="code" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{data.text_body}</pre>
              )}
          </div>
        </>
      )}

      {data.webhook_deliveries?.length > 0 && (
        <>
          <h2>Webhook deliveries</h2>
          <p className="small muted" style={{ marginTop: -8, marginBottom: 12 }}>
            Each event we POSTed to your endpoints for this message, and what they answered. A 2xx
            is a success on your side; anything else is retried and shown here.
          </p>
          <div className="card flush">
            <table className="data">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Endpoint</th>
                  <th className="num">Response</th>
                  <th className="num">Attempt</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {data.webhook_deliveries.map((w: any, i: number) => {
                  const ok = w.succeeded
                  const code = w.status_code
                  return (
                    <tr key={w.event_id + i}>
                      <td><span className="badge neutral">{w.event_type}</span></td>
                      <td className="mono small truncate" style={{ maxWidth: 320 }} title={w.url}>{w.url}</td>
                      <td className="num">
                        <span className={`badge ${ok ? 'ok' : w.dead_lettered ? 'critical' : 'pending'}`}>
                          {ok ? `${code ?? 'OK'}` : w.dead_lettered ? 'Dead-lettered' : (code ? `HTTP ${code}` : (w.error ?? 'Failed'))}
                        </span>
                        {w.duration_ms != null && <span className="small muted"> · {w.duration_ms}ms</span>}
                      </td>
                      <td className="num">{w.attempt}</td>
                      <td className="small muted" title={new Date(w.at).toLocaleString()}>{relTime(w.at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {recipients.length > 0 && (
        <>
          <h2>Recipients</h2>
          <div className="card flush">
            <table className="data">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="num">Attempts</th>
                  <th>Receiver response</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r: any) => {
                  const rs = eventBadge(r.status)
                  const kind = (r.kind ?? 'to').toLowerCase()
                  return (
                    <tr key={`${r.address}-${kind}`}>
                      <td className="mono small">{r.address}</td>
                      <td><span className={`badge ${kind === 'bcc' ? 'neutral' : kind === 'cc' ? 'info' : 'ok'}`}>{kind.toUpperCase()}</span></td>
                      <td><span className={`badge ${rs.cls}`}>{rs.text}</span></td>
                      <td className="num">{r.attempts ?? 0}</td>
                      <td className="small muted mono">
                        {/* The receiver's own words. This is what a customer needs to
                            paste into a support ticket, so it is shown verbatim rather
                            than translated into our vocabulary. */}
                        {r.response
                          ? <>{r.smtp_code} {r.response}</>
                          : <span className="muted">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2>Timeline</h2>
      {events.length === 0
        ? (
          <div className="hint">
            No events recorded yet. Delivery events arrive asynchronously — a receiver can
            take minutes, and a deferral can retry for days before resolving.
          </div>
        )
        : (
          <ol className="timeline">
            {events.map((e: any, i: number) => {
              const el = eventBadge(e.type)
              return (
                <li key={i} className={el.cls}>
                  <div className="t-head">
                    <span className={`badge ${el.cls}`}>{el.text}</span>
                    <span className="small muted">
                      {new Date(e.at).toLocaleString()}
                    </span>
                  </div>
                  {e.recipient && (
                    <div className="small mono">{e.recipient}</div>
                  )}
                  {(e.smtp_code || e.response) && (
                    <div className="small muted mono">
                      {e.smtp_code} {e.dsn} {e.response}
                    </div>
                  )}
                  {e.remote_mx && (
                    <div className="small muted">via {e.remote_mx}</div>
                  )}
                  {e.detail && <div className="small muted">{e.detail}</div>}
                </li>
              )
            })}
          </ol>
        )}
    </div>
  )
}
